import { Form, type FormInstance } from 'antd';

import { handleEnterKeyNavigation, useAutoFocusFirstField } from './enterKeyNavigation';
import { Button } from './PageHeading';
import { useEscapeKey } from './shortcuts';

/**
 * The create/edit drawer every master screen opens.
 *
 * The chrome is the template's overlay — same border, radius, header rule and footer — but it is
 * **React-controlled rather than Preline-controlled**, and that is a deliberate departure. Preline
 * opens overlays from `data-hs-overlay="#id"` against an element that must already be in the DOM.
 * Ours is mounted only while something is being edited, because the form's initial values come
 * from the row that was clicked; a permanently mounted drawer would need its fields reset by hand
 * on every open, which is exactly the bug that leaves yesterday's values in today's form.
 *
 * Ant Design supplies the form engine underneath: validation rules, error placement, submit state.
 * The template has markup for inputs and none for any of that, and re-implementing validation
 * seven times is how two screens end up disagreeing about whether a GSTIN is required.
 */
export function FormDrawer<Values extends object>({
  title,
  subtitle,
  open,
  onClose,
  form,
  initialValues,
  onSubmit,
  submitText,
  submitting = false,
  width = 520,
  children,
}: {
  title: string;
  // `| undefined` spelled out rather than relying on `?`: `exactOptionalPropertyTypes` is on, so a
  // caller passing a conditional (`isEdit ? undefined : '…'`) is otherwise a type error at every
  // call site rather than here, once.
  subtitle?: string | undefined;
  open: boolean;
  onClose: () => void;
  form: FormInstance<Values>;
  initialValues?: Partial<Values> | undefined;
  onSubmit: (values: Values) => void;
  submitText: string;
  submitting?: boolean;
  width?: number;
  children: React.ReactNode;
}) {
  // Escape closes it. Expected of anything modal, and the only alternative here is the mouse —
  // these screens are used at a counter where the keyboard is the fast path. Shared with the two
  // hand-rolled drawers, which had no way out but the mouse until it was pulled out of here.
  useEscapeKey(onClose, open);

  // The cursor starts in the first field, so Enter and Alt+S have somewhere to work from without a
  // click first. One rule for all eleven drawers rather than an `autoFocus` per form.
  const fields = useAutoFocusFirstField<HTMLDivElement>(open);

  if (!open) {
    return null;
  }

  return (
    // `data-shortcut-layer`: while this is open, Alt+S is *this* drawer's Save. Without it the key
    // would reach past the drawer and commit the screen behind — which the person cannot see.
    <div
      data-shortcut-layer
      className="fixed inset-0 z-9999 flex justify-end"
      role="dialog"
      aria-modal="true"
    >
      {/*
        The scrim does not close the drawer on click, and that is not an oversight. These forms
        carry a dozen fields; a stray click on the page behind, after five minutes of typing,
        should not discard them. Escape and Cancel are the two ways out, and both are deliberate.
      */}
      <div className="absolute inset-0 bg-[var(--overlay-scrim)]" aria-hidden="true" />

      <div
        className="relative flex h-full flex-col border-l border-[var(--overlay-border)] bg-[var(--overlay-surface)] shadow-lg max-sm:w-full"
        style={{ width }}
      >
        <div className="flex items-start justify-between gap-4 border-b border-border-color p-4">
          <div>
            <h2 className="text-base font-bold text-gray-900">{title}</h2>
            {subtitle && <p className="mt-0.5 text-[12px] text-default">{subtitle}</p>}
          </div>

          <button
            type="button"
            className="inline-flex size-7 shrink-0 cursor-pointer items-center justify-center rounded-md border border-border-color hover:bg-light"
            aria-label="Close"
            onClick={onClose}
          >
            <i className="icon icon-x text-sm" />
          </button>
        </div>

        <Form<Values>
          form={form}
          layout="vertical"
          // Spread rather than passed: under `exactOptionalPropertyTypes` an explicit `undefined`
          // is not the same as an absent prop, and Ant Design types this one as required-if-present.
          {...(initialValues ? { initialValues } : {})}
          onFinish={onSubmit}
          requiredMark={false}
          // Enter moves to the next field and the last field hands focus to Save, the way an ERP
          // data-entry screen behaves — see `enterKeyNavigation`. One handler on the form covers
          // every control inside it by bubbling, including the hand-rolled inputs some drawers
          // render instead of `Form.Item`s.
          onKeyDown={handleEnterKeyNavigation}
          className="flex min-h-0 flex-1 flex-col"
        >
          {/* The ref is on the fields, not the form: the footer's buttons are not somewhere to
              start typing, and "first field" has to mean the first one of those. */}
          <div ref={fields} className="min-h-0 flex-1 overflow-y-auto p-4">
            {children}
          </div>

          {/*
            The footer is pinned rather than scrolled with the fields. On the longer forms — the
            branch form is fourteen fields — a submit button that has to be scrolled to is one the
            user is not sure exists.
          */}
          <div className="flex items-center justify-end gap-2 border-t border-border-color bg-light p-4">
            <Button onClick={onClose}>Cancel</Button>
            <Button variant="primary" type="submit" shortcut="save" disabled={submitting}>
              {submitting ? 'Saving…' : submitText}
            </Button>
          </div>
        </Form>
      </div>
    </div>
  );
}
